import { WorkbookPage, SectionTitle, Paragraph } from '@/components/WorkbookPage';
import { Users } from 'lucide-react';

export const ProgramDescriptionPage = () => {
  return (
    <WorkbookPage pageId="program-description" prevPage="reset-page" nextPage="facilitator-note">
      <header className="mb-10">
        <div className="flex items-center gap-3 mb-4">
          <Users className="h-6 w-6 text-primary" />
          <h1 className="text-3xl md:text-4xl font-serif font-bold text-foreground">
            Program Description
          </h1>
        </div>
        <p className="text-muted-foreground">
          For group facilitators and organizations
        </p>
      </header>
      
      <SectionTitle>About This Workbook</SectionTitle>
      
      <Paragraph>
        <strong>The Rules of Money Workbook</strong> is a comprehensive financial literacy 
        program designed for adults and teens of all backgrounds. The content is written 
        at a 7th-grade reading level, making it accessible while still being thorough and 
        practical.
      </Paragraph>
      
      <Paragraph>
        This workbook takes a trauma-informed, shame-free approach to personal finance. 
        We recognize that money is emotional, that many people carry financial trauma, and 
        that judgment-free education is most effective for lasting change.
      </Paragraph>
      
      <SectionTitle>Program Structure</SectionTitle>
      
      <div className="my-6 space-y-4">
        <div className="border border-border rounded-lg p-4">
          <h4 className="font-semibold mb-2">Section I: Stability</h4>
          <p className="text-sm text-muted-foreground">
            Income tracking, multiple accounts, bill planning, spending reduction
          </p>
        </div>
        <div className="border border-border rounded-lg p-4">
          <h4 className="font-semibold mb-2">Section II: Control</h4>
          <p className="text-sm text-muted-foreground">
            Emergency funds, irregular expenses, lump sums, debt snowball
          </p>
        </div>
        <div className="border border-border rounded-lg p-4">
          <h4 className="font-semibold mb-2">Section III: Growth</h4>
          <p className="text-sm text-muted-foreground">
            Credit understanding, housing decisions, smart borrowing
          </p>
        </div>
        <div className="border border-border rounded-lg p-4">
          <h4 className="font-semibold mb-2">Section IV: Healing & Purpose</h4>
          <p className="text-sm text-muted-foreground">
            Money stories, values alignment, entrepreneurship basics
          </p>
        </div>
        <div className="border border-border rounded-lg p-4">
          <h4 className="font-semibold mb-2">Section V: Capstone</h4>
          <p className="text-sm text-muted-foreground">
            Personal Life & Money Map, commitment statement
          </p>
        </div>
        <div className="border border-border rounded-lg p-4">
          <h4 className="font-semibold mb-2">Section VI: Appendices</h4>
          <p className="text-sm text-muted-foreground">
            Glossary, trackers, worksheets, crisis mode rules
          </p>
        </div>
      </div>
      
      <SectionTitle>Recommended Use</SectionTitle>
      
      <ul className="space-y-2 my-6">
        <li className="flex items-start gap-3">
          <span className="text-primary font-bold">•</span>
          <span><strong>Individual study:</strong> Self-paced, 1-2 rules per week recommended</span>
        </li>
        <li className="flex items-start gap-3">
          <span className="text-primary font-bold">•</span>
          <span><strong>Group classes:</strong> 12-16 week program, 1 rule per session</span>
        </li>
        <li className="flex items-start gap-3">
          <span className="text-primary font-bold">•</span>
          <span><strong>Workshop format:</strong> Weekend intensive covering key sections</span>
        </li>
        <li className="flex items-start gap-3">
          <span className="text-primary font-bold">•</span>
          <span><strong>Support groups:</strong> Weekly meetings to discuss progress and challenges</span>
        </li>
      </ul>
      
      <SectionTitle>Who This Is For</SectionTitle>
      
      <div className="grid md:grid-cols-2 gap-4 my-6">
        <div className="bg-secondary/50 rounded-lg p-4">
          <ul className="text-sm space-y-2">
            <li>• Adults rebuilding after financial setbacks</li>
            <li>• Young adults learning money basics</li>
            <li>• People with irregular or low income</li>
            <li>• Those recovering from debt</li>
          </ul>
        </div>
        <div className="bg-secondary/50 rounded-lg p-4">
          <ul className="text-sm space-y-2">
            <li>• Community organizations</li>
            <li>• Churches and faith groups</li>
            <li>• Social service agencies</li>
            <li>• Schools and educational programs</li>
          </ul>
        </div>
      </div>
      
      <div className="mt-8 bg-primary/5 rounded-xl p-6">
        <p className="text-sm text-muted-foreground">
          <strong>Note:</strong> This workbook is for educational purposes. It is not a 
          substitute for professional financial, legal, or tax advice. Encourage participants 
          to seek professional guidance for complex situations.
        </p>
      </div>
    </WorkbookPage>
  );
};
